import {
  addElement,
  defaultGenericElement,
  getNextRetouchLayer,
  reversionElement,
  type GenericElement,
} from '../../lib/element';
import {
  prepareElementForExport,
  roundExportVal,
} from '../../lib/exportImport';
import { type Plugin } from '../../lib/plugin';
import { updateRuntimeStore } from '../../stores/useEditorRuntimeStore';
import { useEditorStore } from '../../stores/useEditorStore';
import { clipToMainImage } from '../../utils/canvas';
import { distance, pointDistance, type Point } from '../../utils/math';
import { translateSvgPathWithSubpaths } from '../../utils/svg';
import {
  type GenericFreedrawElement,
  freedraw,
  type StrokeCap,
} from '../freedraw';
import { getFreeDrawElementAbsoluteCoords } from '../freedraw/utils/coords';
import { simplifyPoints } from '../freedraw/utils/points';
import { getFreedrawShape } from '../freedraw/utils/shape';
import { general } from '../general';

import { renderRetouchCanvas } from './render';

type Mask = {
  data: string;
  dx: number;
  dy: number;
};

export interface CloneStampElement extends GenericFreedrawElement {
  type: 'cloneStamp';
  sourcePoint: Point;
  opacity: number;
  mask: Mask | null;
}

const state: {
  sourcePoint: Point | null;
  lastStrokeEndPoint: Point | null;
  strokeWidth: number;
  strokeCap: StrokeCap;
  opacity: number;
  limitToMask: boolean;
} = {
  sourcePoint: null,
  lastStrokeEndPoint: null,
  strokeWidth: 40,
  strokeCap: 'round',
  opacity: 1,
  limitToMask: false,
};

const actions = {
  activate: () => {
    general.actions.activate(plugin.name, true, false);
  },
  setStrokeWidth: (width: number) => {
    state.strokeWidth = width;
  },
  setStrokeCap: (cap: StrokeCap) => {
    state.strokeCap = cap;
  },
  setOpacity: (opacity: number) => {
    state.opacity = opacity;
  },
  setLimitToMask: (limit: boolean) => {
    state.limitToMask = limit;
  },
  setSourcePoint: (point: Point | null) => {
    state.sourcePoint = point;
    state.lastStrokeEndPoint = null;
  },
};

const MIN_POINT_DRAW_DISTANCE = 3;

const plugin: Plugin<CloneStampElement, typeof actions, typeof state> = {
  name: 'cloneStamp',
  cursor: 'crosshair',
  actions,
  state: state,
  events: {
    onPointerDown: ({ event, helpers, runtimeState }) => {
      const { button, pos, original } = event;

      if (button === 'left' && runtimeState.activeTool === 'cloneStamp') {
        if (original.altKey || original.ctrlKey) {
          state.sourcePoint = [pos.x, pos.y];
          state.lastStrokeEndPoint = null;
          helpers.renderInteractiveScene();
          return;
        }

        if (!state.sourcePoint) return;

        const activeElementId = useEditorStore.getState().selectedElementIds;
        const activeElement = activeElementId[0]
          ? useEditorStore.getState().elements.get(activeElementId[0])
          : null;

        let adjustedSourcePoint = state.sourcePoint;
        if (state.lastStrokeEndPoint) {
          const offsetX = pos.x - state.lastStrokeEndPoint[0];
          const offsetY = pos.y - state.lastStrokeEndPoint[1];
          adjustedSourcePoint = [
            state.sourcePoint[0] + offsetX,
            state.sourcePoint[1] + offsetY,
          ];
        }

        helpers.updateRuntimeStore({
          newElement: {
            ...defaultGenericElement(),
            layer: getNextRetouchLayer(),
            isLocked: true,
            originX: pos.x,
            originY: pos.y,
            points: [[0, 0]],
            strokeColor: 'rgba(0, 0, 0, 1)',
            strokeWidth: state.strokeWidth,
            strokeCap: state.strokeCap,
            type: 'cloneStamp',
            x: pos.x,
            y: pos.y,
            booleanMode: 'union',
            sourcePoint: adjustedSourcePoint,
            opacity: state.opacity,
            mask:
              state.limitToMask && activeElement != null
                ? {
                    data:
                      activeElement.type !== 'boolean'
                        ? translateSvgPathWithSubpaths(
                            activeElement.shape.data,
                            -activeElement.x,
                            -activeElement.y,
                          )
                        : activeElement.shape.data,
                    dx: activeElement.x,
                    dy: activeElement.y,
                  }
                : null,
          } as CloneStampElement,
        });

        helpers.showNewElementCanvas();
        helpers.renderNewElementScene();
      }
    },
    onPointerMove: ({ event, helpers, runtimeState }) => {
      const { button, pos } = event;
      if (
        button === 'left' &&
        runtimeState.newElement &&
        runtimeState.newElement.type === 'cloneStamp'
      ) {
        const element = runtimeState.newElement as CloneStampElement;
        const newPoint: Point = [pos.x - element.x, pos.y - element.y];
        const lastPoint = element.points.at(-1);

        const distanceThreshold =
          MIN_POINT_DRAW_DISTANCE / runtimeState.zoom.value;

        if (
          lastPoint &&
          pointDistance(newPoint, lastPoint) < distanceThreshold
        ) {
          return;
        }

        const newElement = {
          ...element,
          ...reversionElement(element, ['bounds', 'canvas']),
          points: [...element.points, newPoint],
        } as GenericElement;

        const [x1, y1, x2, y2] = getFreeDrawElementAbsoluteCoords(
          newElement as CloneStampElement,
        );
        const width = distance(x1, x2);
        const height = distance(y1, y2);

        helpers.updateRuntimeStore({
          newElement: { ...newElement, height, width },
        });
        helpers.renderNewElementScene();
      }
    },
    onPointerUp: ({ elementState, helpers, history, runtimeState }) => {
      if (!runtimeState.newElement || runtimeState.activeTool !== 'cloneStamp')
        return;

      const newElement = runtimeState.newElement as CloneStampElement;

      const simplified = simplifyPoints(newElement.points);

      const freedrawElement = {
        ...runtimeState.newElement,
        points: simplified.points,
        bezierHandles: simplified.bezierHandles,
        shape: getFreedrawShape({ ...newElement, ...simplified }),
      };

      if (newElement.sourcePoint && simplified.points.length > 0) {
        const firstPoint = simplified.points[0];
        const lastPoint = simplified.points[simplified.points.length - 1];
        const offsetX = lastPoint[0] - firstPoint[0];
        const offsetY = lastPoint[1] - firstPoint[1];

        state.sourcePoint = [
          newElement.sourcePoint[0] + offsetX,
          newElement.sourcePoint[1] + offsetY,
        ];
        state.lastStrokeEndPoint = [
          newElement.x + lastPoint[0],
          newElement.y + lastPoint[1],
        ];
      }

      addElement(elementState.elements, freedrawElement);
      history.push(elementState);
      updateRuntimeStore({ newElement: undefined });

      helpers.renderStaticScene();
      helpers.hideNewElementCanvas();
    },
    blockTouchPanning: true,
  },
  render: {
    ...freedraw.render,
    draw: ({ config, context, element, runtimeState, elementState }) => {
      if (!element.points.length) return;

      context.save();

      clipToMainImage(
        context,
        elementState.elements,
        element,
        element.strokeWidth / 2,
      );

      if (element.mask != null) {
        const clipPath = new Path2D(
          translateSvgPathWithSubpaths(
            element.mask.data,
            element.mask.dx - element.x + element.strokeWidth / 2,
            element.mask.dy - element.y + element.strokeWidth / 2,
          ),
        );
        context.clip(clipPath);
      }

      if (element.isDeletedFlag) {
        context.globalAlpha = config.canvas.deletingElementOpacity;
      } else {
        context.globalAlpha = element.opacity;
      }

      const zoom = runtimeState.zoom.value;

      const retouchCanvasResult = renderRetouchCanvas(element.layer, zoom);

      if (!retouchCanvasResult) {
        context.restore();
        return;
      }

      const {
        canvas: retouchCanvas,
        renderZoom,
        offsetX,
        offsetY,
      } = retouchCanvasResult;

      const padding =
        element.strokeCap === 'square'
          ? element.strokeWidth * Math.SQRT2
          : element.strokeWidth;

      const minX = Math.min(...element.points.map(([x]) => x));
      const minY = Math.min(...element.points.map(([, y]) => y));
      const maxX = Math.max(...element.points.map(([x]) => x));
      const maxY = Math.max(...element.points.map(([, y]) => y));

      const stampWidth = maxX - minX + padding;
      const stampHeight = maxY - minY + padding;

      const points = element.points.map((point) => [
        point[0] - minX + padding / 2,
        point[1] - minY + padding / 2,
      ]);

      const firstPointAbs = [
        element.x + element.points[0][0] + padding / 2,
        element.y + element.points[0][1] + padding / 2,
      ];

      const sceneOffsetX = element.sourcePoint[0] - firstPointAbs[0];
      const sceneOffsetY = element.sourcePoint[1] - firstPointAbs[1];

      const elementSceneX = element.x + minX;
      const elementSceneY = element.y + minY;

      const sourceSceneX = elementSceneX + sceneOffsetX;
      const sourceSceneY = elementSceneY + sceneOffsetY;

      const sourceRegionX = (sourceSceneX - offsetX) * renderZoom;
      const sourceRegionY = (sourceSceneY - offsetY) * renderZoom;
      const sourceRegionWidth = stampWidth * renderZoom;
      const sourceRegionHeight = stampHeight * renderZoom;

      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = stampWidth;
      tempCanvas.height = stampHeight;
      const tempContext = tempCanvas.getContext('2d')!;

      try {
        tempContext.drawImage(
          retouchCanvas,
          sourceRegionX,
          sourceRegionY,
          sourceRegionWidth,
          sourceRegionHeight,
          0,
          0,
          stampWidth,
          stampHeight,
        );
      } catch {
        context.restore();
        return;
      }

      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = stampWidth;
      maskCanvas.height = stampHeight;
      const maskContext = maskCanvas.getContext('2d')!;

      maskContext.lineWidth = element.strokeWidth;
      maskContext.lineCap = element.strokeCap ?? 'round';
      maskContext.lineJoin = element.strokeCap === 'round' ? 'round' : 'miter';

      maskContext.beginPath();
      maskContext.moveTo(points[0][0], points[0][1]);

      for (let i = 1; i < points.length - 1; i++) {
        const [x1, y1] = points[i];
        const [x2, y2] = points[i + 1];

        const midX = (x1 + x2) / 2;
        const midY = (y1 + y2) / 2;

        maskContext.quadraticCurveTo(x1, y1, midX, midY);
      }

      const lastPoint = points[points.length - 1];
      maskContext.lineTo(lastPoint[0], lastPoint[1]);

      maskContext.strokeStyle = 'white';
      maskContext.stroke();

      tempContext.globalCompositeOperation = 'destination-in';
      tempContext.drawImage(maskCanvas, 0, 0);

      context.drawImage(tempCanvas, minX, minY);

      context.restore();
    },
    isRetouchTool: true,
  },
  export: (element) => {
    const prepared = prepareElementForExport(
      element,
    ) as Partial<CloneStampElement>;

    prepared.points = element.points.map(([x, y]) => [
      roundExportVal(x),
      roundExportVal(y),
    ]);

    return prepared;
  },
  collision: {
    onlyCountDirectElementHitAfterSelection: false,
    bounds: freedraw.collision.bounds,
    calculateShape: () => ({ data: '', type: 'line' }),
    isPointInShape: () => false,
    isPointOnShape: () => false,
  },
  transformation: {
    shiftBySizeOnFlip: false,
    resizedBounds: () => [0, 0, 0, 0, 0, 0],
  },
  import: (element) => {
    const imported = {
      ...defaultGenericElement(),
      ...element,
      mask: element.mask ?? null,
    } as CloneStampElement;

    imported.shape = getFreedrawShape(imported);

    return imported;
  },
};

export const cloneStamp = plugin;
